import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatError } from "../lib/api";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { PageHeader, GradientCard } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { fmtDateTimeIST, fmtDateIST } from "../lib/datetime";
import UserPickerDialog from "../components/UserPickerDialog";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { tabAccessibleFor, subtabAccessibleFor } from "../lib/modules";
import {
  Megaphone, Plus, Trash2, Paperclip, Download, ExternalLink,
  ShieldCheck, Users, UserPlus, X,
  Clock, CheckCircle2, XCircle, AlertTriangle, Timer, Calendar,
  Info, Pencil, FileText, Zap,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Temp / restricted user roles that must NEVER receive Editor-Corner access.
const TEMP_ROLE_KEYS = ["ex_employee_temp", "candidate_temp"];

// Live countdown ticker — updates every second.
function useTick(intervalMs = 1000) {
  const [, force] = useState(0);
  useEffect(() => { const t = setInterval(() => force((n) => n + 1), intervalMs); return () => clearInterval(t); }, [intervalMs]);
}

const secondsToHms = (s) => {
  if (s == null) return "";
  const total = Math.max(0, Math.floor(s));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  if (m <= 0) return `${sec}s`;
  return `${m}m ${sec.toString().padStart(2, "0")}s`;
};

// ---------------- helpers ----------------

const statusBadgeColor = (s) => ({
  draft:    "bg-slate-100 text-slate-600",
  approved: "bg-emerald-100 text-emerald-800",
  pending:  "bg-amber-100 text-amber-800",
  rejected: "bg-rose-100 text-rose-800",
  scheduled:"bg-sky-100 text-sky-800",
  expired:  "bg-slate-200 text-slate-700",
}[s] || "bg-slate-200 text-slate-700");

const roleLabel = (r) => ({
  master_admin: "Master Admin", hr_admin: "HR Admin", sales_admin: "Sales Admin",
  sales_team: "Sales Team", accounts_admin: "Accounts Admin",
  dispatch_admin: "Dispatch Admin", it_admin: "IT Admin",
  crm_manager: "CRM Manager", documentation_team: "Documentation",
  trainer: "Trainer", employee: "Employee",
}[r] || r);

const countdownLabel = (iso) => {
  if (!iso) return "";
  const target = new Date(iso).getTime();
  const now = Date.now();
  const diff = target - now;
  if (diff <= 0) return "any moment";
  const mins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  if (mins <= 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
};

// ---------------- Live countdown badge ----------------
// Consumes `announcement.seconds_left_to_publish` / `seconds_left_to_schedule`
// as a starting point (captured at fetch time) and decrements every second.
function CountdownPill({ seconds, label, icon: Icon, tone = "amber" }) {
  const [left, setLeft] = useState(seconds ?? 0);
  useEffect(() => { setLeft(seconds ?? 0); }, [seconds]);
  useEffect(() => {
    if (left <= 0) return;
    const t = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [left]);
  const cls = tone === "sky"
    ? "bg-sky-50 text-sky-800 border-sky-200"
    : "bg-amber-50 text-amber-900 border-amber-200";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {Icon ? <Icon className="w-3 h-3" /> : null}
      {label}: <strong className="font-mono">{left > 0 ? secondsToHms(left) : "any moment"}</strong>
    </span>
  );
}

// ---------------- Announcement card ----------------

function AnnouncementCard({ ann, onOpen, onDelete, onEdit, canDelete, canEdit }) {
  const now = Date.now();
  const endMs = ann.end_at ? new Date(ann.end_at).getTime() : null;
  const secondsToEnd = endMs != null ? Math.max(0, Math.floor((endMs - now) / 1000)) : null;

  // Lifecycle tag: Draft | Published | Scheduled
  const lifecycle = ann.status === "draft" ? "Draft"
    : ann.status === "scheduled" ? "Scheduled"
    : ann.status === "expired" ? "Expired"
    : ann.status === "approved" ? "Published"
    : null;
  const lifecycleClass = ann.status === "draft" ? "bg-slate-200 text-slate-700"
    : ann.status === "scheduled" ? "bg-sky-100 text-sky-800 border-sky-200"
    : ann.status === "expired" ? "bg-slate-200 text-slate-500"
    : ann.status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : "bg-slate-100 text-slate-700";

  // Post-Status tag: Pending | Approved | Rejected (approval workflow)
  const postStatus = ["pending"].includes(ann.status) ? "Pending"
    : ann.status === "rejected" ? "Rejected"
    : ["approved", "scheduled", "expired"].includes(ann.status) ? "Approved"
    : null;
  const postStatusClass = postStatus === "Pending" ? "bg-amber-100 text-amber-900 border-amber-200"
    : postStatus === "Rejected" ? "bg-rose-100 text-rose-800 border-rose-200"
    : postStatus === "Approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : "bg-slate-100 text-slate-700";

  return (
    <div
      onClick={() => onOpen(ann)}
      data-testid={`ann-card-${ann.id}`}
      className="cursor-pointer rounded-2xl border border-slate-200 bg-white/90 backdrop-blur p-4 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all"
    >
      {/* Row 1: lifecycle + important + unread/read + auto-approved tags */}
      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
        {lifecycle && (
          <Badge className={`${lifecycleClass} border font-medium`}>
            {lifecycle}
          </Badge>
        )}
        {ann.important && (
          <Badge className="bg-rose-100 text-rose-800 border-rose-200 gap-1">
            <AlertTriangle className="w-3 h-3" /> Important
          </Badge>
        )}
        {ann.auto_approved && (
          <Badge className="bg-purple-100 text-purple-800 border-purple-200 gap-1">
            <Zap className="w-3 h-3" /> Self-Approved
          </Badge>
        )}
        {/* Unread / Read tag — shown outside the post body, on the card itself.
            Non-important tracks read; Important tracks acknowledgement. */}
        {ann.status === "approved" && !ann.important && (
          ann.i_read ? (
            <Badge className="bg-slate-100 text-slate-700 border-slate-200 gap-1 dark:bg-slate-800 dark:text-slate-300">
              <CheckCircle2 className="w-3 h-3" /> Read
            </Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-900 border-amber-300 gap-1 shadow-[0_0_0_2px_rgba(251,191,36,0.25)]">
              <AlertTriangle className="w-3 h-3" /> Unread
            </Badge>
          )
        )}
        {ann.status === "approved" && ann.important && (
          ann.i_acknowledged ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1">
              <CheckCircle2 className="w-3 h-3" /> Acknowledged
            </Badge>
          ) : (
            <Badge className="bg-rose-100 text-rose-900 border-rose-300 gap-1 shadow-[0_0_0_2px_rgba(244,63,94,0.25)]">
              <AlertTriangle className="w-3 h-3" /> Awaiting acknowledgement
            </Badge>
          )
        )}
      </div>

      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-base font-semibold text-slate-900 truncate">{ann.title}</div>
          <div className="text-[13px] text-slate-600 line-clamp-2 mt-1">{ann.body}</div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {canEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(ann); }}
              className="text-slate-500 hover:text-emerald-600"
              data-testid={`ann-edit-${ann.id}`}
              title="Edit"
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(ann); }}
              className="text-rose-500 hover:text-rose-700"
              data-testid={`ann-delete-${ann.id}`}
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Row 2: live countdowns */}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {ann.status === "scheduled" && ann.seconds_left_to_schedule != null && (
          <CountdownPill seconds={ann.seconds_left_to_schedule} label="Publish in" icon={Calendar} tone="sky" />
        )}
        {ann.status === "approved" && secondsToEnd != null && (
          <CountdownPill seconds={secondsToEnd} label="Ends in" icon={Timer} tone="sky" />
        )}
        {ann.status === "pending" && !ann.important && ann.seconds_left_to_publish != null && (
          <CountdownPill seconds={ann.seconds_left_to_publish} label="Auto-post in" icon={Timer} />
        )}
      </div>

      {/* Row 3: Post-Status tag with meta */}
      {postStatus && (
        <div className={`mt-2 rounded-lg border px-2.5 py-1.5 text-[11.5px] ${postStatusClass}`}>
          <span className="font-semibold">Post status:</span>{" "}
          <span className="font-medium">{postStatus}</span>
          {postStatus === "Pending" && ann.important && (
            <span> · Waiting for Master Admin</span>
          )}
          {postStatus === "Pending" && !ann.important && ann.seconds_left_to_publish != null && (
            <span> · auto-post in <strong className="font-mono">{secondsToHms(ann.seconds_left_to_publish)}</strong></span>
          )}
          {postStatus === "Approved" && ann.approved_by_name && (
            <span> · by {ann.auto_approved ? "auto-approval (15-min timeout)" : ann.approved_by_name}</span>
          )}
          {postStatus === "Rejected" && ann.rejection_reason && (
            <div className="mt-0.5"><span className="font-semibold">Reason:</span> {ann.rejection_reason}</div>
          )}
        </div>
      )}

      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[12px] text-slate-600">
        <div><span className="text-slate-400">By:</span> <strong>{ann.created_by_name}</strong></div>
        <div><span className="text-slate-400">Role:</span> {roleLabel(ann.created_by_role)}{ann.created_by_designation ? ` · ${ann.created_by_designation}` : ""}</div>
        <div><span className="text-slate-400">Created:</span> {fmtDateTimeIST(ann.created_at)}</div>
        <div><span className="text-slate-400">Ends:</span> {fmtDateTimeIST(ann.end_at)}</div>
      </div>
      <div className="mt-2 flex items-center justify-between text-[12px]">
        <div className="text-slate-500">
          {ann.important
            ? `${ann.ack_count || 0} acknowledged`
            : `${ann.read_count || 0} read`}
        </div>
        {ann.attachment_id ? (
          <div className="flex items-center gap-1 text-slate-500"><Paperclip className="w-3.5 h-3.5" /> Attachment</div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------- Announcement list (horizontal / card view) ----------------

function AnnouncementList({ items, view, onOpen, onDelete, onEdit, currentUserId, isMaster }) {
  const canEditRow = (a) => {
    if (isMaster) return true;
    if (a.created_by_id !== currentUserId) return false;
    return ["draft", "pending", "rejected"].includes(a.status);
  };
  // Master Admin has unconditional delete permission (any status).
  const canDeleteRow = (a) => {
    if (isMaster) return true;
    if (a.created_by_id !== currentUserId) return false;
    return ["draft", "pending", "rejected"].includes(a.status);
  };
  if (!items.length) {
    return <div className="rounded-xl border border-dashed border-slate-300 bg-white/70 p-8 text-center text-slate-500">No announcements here.</div>;
  }
  if (view === "list") {
    return (
      <div
        className="rounded-2xl border border-slate-200 bg-white/95 overflow-x-auto overflow-y-hidden"
        style={{ scrollbarWidth: "none" }}
      >
        <style>{`.hide-scroll::-webkit-scrollbar { display: none; }`}</style>
        <table className="min-w-full text-[13px] hide-scroll">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Title</th>
              <th className="px-3 py-2 text-left font-medium">Created By</th>
              <th className="px-3 py-2 text-left font-medium">Role · Designation</th>
              <th className="px-3 py-2 text-left font-medium">Created</th>
              <th className="px-3 py-2 text-left font-medium">Ends</th>
              <th className="px-3 py-2 text-left font-medium">Ack</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
              <th className="px-3 py-2 text-left font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/70 cursor-pointer" onClick={() => onOpen(a)} data-testid={`ann-row-${a.id}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {a.important && <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />}
                    <span className="font-medium text-slate-900">{a.title}</span>
                  </div>
                </td>
                <td className="px-3 py-2">{a.created_by_name}</td>
                <td className="px-3 py-2">{roleLabel(a.created_by_role)}{a.created_by_designation ? ` · ${a.created_by_designation}` : ""}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fmtDateTimeIST(a.created_at)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{fmtDateTimeIST(a.end_at)}</td>
                <td className="px-3 py-2">{a.ack_count || 0}</td>
                <td className="px-3 py-2"><Badge variant="secondary" className={statusBadgeColor(a.status)}>{a.status}</Badge></td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 justify-end">
                    {canEditRow(a) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onEdit(a); }}
                        className="text-slate-500 hover:text-emerald-600"
                        data-testid={`ann-edit-${a.id}`}
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {canDeleteRow(a) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onDelete(a); }}
                        className="text-rose-500 hover:text-rose-700"
                        data-testid={`ann-delete-${a.id}`}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  // Card view
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {items.map((a) => (
        <AnnouncementCard
          key={a.id}
          ann={a}
          onOpen={onOpen}
          onDelete={onDelete}
          onEdit={onEdit}
          canDelete={canDeleteRow(a)}
          canEdit={canEditRow(a)}
        />
      ))}
    </div>
  );
}

// ---------------- Detail dialog ----------------

function DetailDialog({ open, onClose, ann, isMaster, onAcknowledge, onReject, onApprove, onMarkRead, accessRole }) {
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");
  useEffect(() => { if (!open) { setRejectMode(false); setReason(""); } }, [open]);
  if (!ann) return null;

  const canApprove = ann.status === "pending" && (accessRole === "master_admin" || (accessRole === "admin" && !ann.important));
  const canReject = canApprove;
  const canAck = ann.status === "approved" && ann.important && !ann.i_acknowledged;
  const canRead = ann.status === "approved" && !ann.important && !ann.i_read;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ann-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {ann.important && <AlertTriangle className="w-5 h-5 text-rose-500" />}
            {ann.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className={statusBadgeColor(ann.status)}>{ann.status}</Badge>
            {ann.important && <Badge className="bg-rose-100 text-rose-800 border-rose-200">Important · mandatory acknowledgement</Badge>}
            {ann.auto_approved && <Badge className="bg-slate-100 text-slate-700 border-slate-200">Auto-approved</Badge>}
          </div>

          <div className="grid grid-cols-2 gap-3 text-[13px]">
            <div><span className="text-slate-400">Created by:</span> <strong>{ann.created_by_name}</strong></div>
            <div><span className="text-slate-400">Role:</span> {roleLabel(ann.created_by_role)}{ann.created_by_designation ? ` · ${ann.created_by_designation}` : ""}</div>
            <div><span className="text-slate-400">Created at:</span> {fmtDateTimeIST(ann.created_at)}</div>
            <div><span className="text-slate-400">Ends at:</span> {fmtDateTimeIST(ann.end_at)}</div>
            {ann.scheduled_for && (
              <div><span className="text-slate-400">Scheduled for:</span> {fmtDateTimeIST(ann.scheduled_for)}</div>
            )}
            {ann.published_at && (
              <div><span className="text-slate-400">Published:</span> {fmtDateTimeIST(ann.published_at)}</div>
            )}
            {ann.auto_approve_at && ann.status === "pending" && !ann.important && (
              <div className="col-span-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900 flex items-center gap-2">
                <Timer className="w-4 h-4" />
                Your post will be auto-posted in <strong>{countdownLabel(ann.auto_approve_at)}</strong> unless an admin acts.
              </div>
            )}
            {ann.important && ann.status === "pending" && (
              <div className="col-span-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-rose-900">
                Important announcements require <strong>Master Admin approval</strong> — they will NOT auto-post.
              </div>
            )}
            {ann.status === "rejected" && ann.rejection_reason && (
              <div className="col-span-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-rose-900">
                <div className="font-medium mb-0.5">Rejected by {ann.rejected_by_name}</div>
                <div className="text-[12px] opacity-80">Reason: {ann.rejection_reason}</div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 whitespace-pre-wrap text-slate-800">
            {ann.body || <span className="text-slate-400">No body.</span>}
          </div>

          {ann.external_url && (
            <a href={ann.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-emerald-700 hover:underline">
              <ExternalLink className="w-4 h-4" /> {ann.external_url}
            </a>
          )}

          {ann.attachment_id && (
            <a
              href={`${BACKEND}/api/announcements/${ann.id}/attachment`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 hover:bg-slate-50"
              data-testid="ann-download-attachment"
            >
              <Download className="w-4 h-4" /> {ann.attachment_name || "Attachment"}
            </a>
          )}

          {/* Audience */}
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="text-[12px] uppercase text-slate-400 mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> Audience</div>
            {ann.target_all ? (
              <Badge className="bg-emerald-100 text-emerald-800">All Employees</Badge>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(ann.target_roles || []).map((r) => (
                  <Badge key={r} variant="secondary" className="bg-slate-100 text-slate-700">{roleLabel(r)}</Badge>
                ))}
                {(ann.target_user_names || []).map((n, i) => (
                  <Badge key={i} variant="secondary" className="bg-blue-50 text-blue-800">{n}</Badge>
                ))}
                {!(ann.target_roles?.length || ann.target_user_names?.length) && (
                  <span className="text-slate-400 text-[12px]">—</span>
                )}
              </div>
            )}
          </div>

          {/* Acknowledgements — Important posts ONLY */}
          {ann.important && (
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-[12px] uppercase text-slate-400 mb-2 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Acknowledged by ({ann.ack_count || 0})
              </div>
              {(ann.acknowledgements || []).length === 0 ? (
                <div className="text-slate-400 text-[12px]">No acknowledgements yet.</div>
              ) : (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {ann.acknowledgements.map((a, i) => (
                    <div key={i} className="flex justify-between text-[12.5px]">
                      <span className="text-slate-700">{a.name}</span>
                      <span className="text-slate-400">{fmtDateTimeIST(a.at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Read receipts — Non-important posts ONLY */}
          {!ann.important && (
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-[12px] uppercase text-slate-400 mb-2 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Read by ({ann.read_count || 0})
              </div>
              {(ann.read_by || []).length === 0 ? (
                <div className="text-slate-400 text-[12px]">Nobody has marked this as read yet.</div>
              ) : (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {ann.read_by.map((r, i) => (
                    <div key={i} className="flex justify-between text-[12.5px]">
                      <span className="text-slate-700">{r.name}</span>
                      <span className="text-slate-400">{fmtDateTimeIST(r.at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-wrap gap-2">
          {canAck && (
            <Button onClick={() => onAcknowledge(ann)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ann-ack-btn">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> I Acknowledge
            </Button>
          )}
          {canRead && onMarkRead && (
            <Button onClick={() => onMarkRead(ann)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ann-read-btn">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> I Read
            </Button>
          )}
          {canApprove && !rejectMode && (
            <>
              <Button onClick={() => onApprove(ann)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ann-approve-btn">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
              </Button>
              <Button variant="destructive" onClick={() => setRejectMode(true)} data-testid="ann-reject-btn">
                <XCircle className="w-4 h-4 mr-1.5" /> Reject
              </Button>
            </>
          )}
          {canReject && rejectMode && (
            <div className="w-full space-y-2">
              <Label>Rejection reason</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explain why this is being rejected" data-testid="ann-reject-reason" />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setRejectMode(false)}>Cancel</Button>
                <Button variant="destructive" onClick={() => onReject(ann, reason)} disabled={!reason.trim()} data-testid="ann-reject-confirm">
                  Confirm Reject
                </Button>
              </div>
            </div>
          )}
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Create/New Post dialog ----------------

const ROLE_OPTIONS = [
  "hr_admin", "sales_admin", "sales_team", "accounts_admin",
  "dispatch_admin", "it_admin", "crm_manager", "documentation_team",
  "trainer", "employee",
];

// Convert an ISO / Date to `YYYY-MM-DDTHH:mm` for datetime-local inputs.
// The <input type="datetime-local"> interprets the value in the browser's
// LOCAL timezone — which is IST for our operations. We format IST wall-clock
// components manually so nothing shifts.
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d);
  const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  const hour = p.hour === "24" ? "00" : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`;
};

function NewPostDialog({ open, onClose, onCreated, users, isMaster, editing }) {
  const [form, setForm] = useState({
    title: "", body: "", external_url: "",
    important: false, target_all: false,
    target_user_ids: [], target_roles: [],
    end_at: "", scheduled_for: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({ title: "", body: "", external_url: "", important: false, target_all: false, target_user_ids: [], target_roles: [], end_at: "", scheduled_for: "" });
      setFile(null);
      return;
    }
    if (editing) {
      setForm({
        title: editing.title || "",
        body: editing.body || "",
        external_url: editing.external_url || "",
        important: !!editing.important,
        target_all: !!editing.target_all,
        target_user_ids: editing.target_user_ids || [],
        target_roles: editing.target_roles || [],
        end_at: toLocalInput(editing.end_at),
        scheduled_for: toLocalInput(editing.scheduled_for),
      });
    }
  }, [open, editing]);

  const activeUsers = useMemo(
    () => users.filter(
      (u) => u.is_active !== false && !u.is_temp_account && !TEMP_ROLE_KEYS.includes(u.role)
    ),
    [users]
  );

  // Toggling a role auto-checks / auto-unchecks every active user with that role.
  const toggleRole = (r) => {
    setForm((f) => {
      const isOn = f.target_roles.includes(r);
      const nextRoles = isOn ? f.target_roles.filter((x) => x !== r) : [...f.target_roles, r];
      const idsOfRole = activeUsers.filter((u) => u.role === r).map((u) => u.id);
      const set = new Set(f.target_user_ids);
      if (isOn) idsOfRole.forEach((id) => set.delete(id));
      else idsOfRole.forEach((id) => set.add(id));
      return { ...f, target_roles: nextRoles, target_user_ids: Array.from(set) };
    });
  };

  const openPicker = () => setPickerOpen(true);
  const onPickDone = (ids) => setForm((f) => ({ ...f, target_user_ids: ids }));

  const submit = async ({ asDraft = false } = {}) => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!form.end_at) { toast.error("End date/time required"); return; }
    if (!asDraft && !form.target_all && form.target_user_ids.length === 0 && form.target_roles.length === 0) {
      toast.error("Pick an audience (all, users, or roles)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("body", form.body);
      fd.append("external_url", form.external_url);
      fd.append("important", form.important ? "true" : "false");
      fd.append("target_all", form.target_all ? "true" : "false");
      fd.append("target_user_ids", form.target_user_ids.join(","));
      fd.append("target_roles", form.target_roles.join(","));
      fd.append("end_at", new Date(form.end_at).toISOString());
      if (form.scheduled_for) fd.append("scheduled_for", new Date(form.scheduled_for).toISOString());
      if (asDraft) fd.append("save_as_draft", "true");
      if (file) fd.append("file", file);
      if (editing) {
        // Editor re-submits: rejected/draft → pending. MA: no resubmit flag.
        if (!isMaster && ["draft", "rejected"].includes(editing.status) && !asDraft) {
          fd.append("resubmit", "true");
        }
        await api.put(`/announcements/${editing.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(asDraft ? "Saved as draft" : "Updated");
      } else {
        const { data } = await api.post("/announcements", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(
          asDraft ? "Saved as draft"
          : data.status === "pending" ? "Submitted for approval"
          : data.status === "scheduled" ? "Scheduled"
          : "Posted"
        );
      }
      onCreated();
      onClose();
    } catch (e) {
      toast.error(formatError(e));
    } finally { setBusy(false); }
  };

  // Names of currently-picked users for the summary chip on the picker button.
  const pickedNames = useMemo(
    () => form.target_user_ids
      .map((id) => activeUsers.find((u) => u.id === id)?.name)
      .filter(Boolean),
    [form.target_user_ids, activeUsers]
  );

  return (
    <>
      <Dialog open={open && !pickerOpen} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ann-new-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Announcement" : "New Announcement"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ann-title-input" />
            </div>
            <div>
              <Label>Body</Label>
              <Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="ann-body-input" />
            </div>
            <div>
              <Label>External URL (optional)</Label>
              <Input placeholder="https://…" value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Visible until (end date/time) *</Label>
                <Input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} data-testid="ann-end-at-input" />
              </div>
              <div>
                <Label>Schedule for (optional)</Label>
                <Input type="datetime-local" value={form.scheduled_for} onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })} data-testid="ann-scheduled-input" />
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2">
              <Switch checked={form.important} onCheckedChange={(v) => setForm({ ...form, important: v })} data-testid="ann-important-switch" />
              <div>
                <div className="font-medium text-rose-800">Mark as Important</div>
                <div className="text-[11.5px] text-rose-700/80">
                  Important announcements show as a mandatory banner. {isMaster ? "Posts instantly." : "Needs Master Admin approval — will NOT auto-post."}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 p-3 space-y-3">
              <div className="text-[12px] uppercase text-slate-400 flex items-center gap-1"><Users className="w-3 h-3" /> Audience</div>
              <div className="flex items-center gap-3">
                <Switch checked={form.target_all} onCheckedChange={(v) => setForm({ ...form, target_all: v })} data-testid="ann-target-all-switch" />
                <span>All Employees ({activeUsers.length})</span>
              </div>
              {!form.target_all && (
                <div>
                  <Label className="text-[12px]">Individual users</Label>
                  <button
                    type="button"
                    onClick={openPicker}
                    className="mt-1 w-full text-left rounded-lg border border-slate-300 bg-white px-3 py-2 hover:border-emerald-400"
                    data-testid="ann-users-picker-btn"
                  >
                    {form.target_user_ids.length === 0 ? (
                      <span className="text-slate-400">Select users…</span>
                    ) : (
                      <span>
                        <strong className="text-emerald-700">{form.target_user_ids.length}</strong> selected
                        <span className="text-slate-500 text-[12px] ml-2">
                          {pickedNames.slice(0, 3).join(", ")}
                          {pickedNames.length > 3 ? `, +${pickedNames.length - 3} more` : ""}
                        </span>
                      </span>
                    )}
                    <span className="float-right text-slate-400">▸ Pick</span>
                  </button>
                  <div className="mt-1 text-[11px] text-slate-500">
                    Tip: inside the picker you can toggle whole roles/teams at once — that auto-checks all matching users.
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label>Attachment (optional)</Label>
              <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ann-file-input" />
              {editing?.attachment_name && !file && (
                <div className="text-[11.5px] text-slate-500 mt-1">Currently attached: <strong>{editing.attachment_name}</strong> (uploading a new file replaces it — leaving blank keeps it).</div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            {(!editing || editing.status === "draft" || (isMaster && ["approved", "scheduled"].includes(editing.status) === false)) && (
              <Button
                variant="secondary"
                onClick={() => submit({ asDraft: true })}
                disabled={busy}
                data-testid="ann-save-draft-btn"
              >
                <FileText className="w-4 h-4 mr-1.5" /> Save as Draft
              </Button>
            )}
            <Button
              onClick={() => submit()}
              disabled={busy}
              data-testid="ann-submit-btn"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {busy
                ? (editing ? "Updating…" : "Posting…")
                : editing
                  ? (isMaster ? "Update" : (editing.status === "rejected" ? "Resubmit" : "Update"))
                  : "Post"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onDone={onPickDone}
        users={activeUsers}
        initialSelectedIds={form.target_user_ids}
        title="Select audience"
        roleOptions={ROLE_OPTIONS}
      />
    </>
  );
}

// ---------------- Tab Access dialog (Master Admin) ----------------

function TabAccessDialog({ open, onClose, users, onSaved }) {
  const [grants, setGrants] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedIds, setPickedIds] = useState([]);
  // Default role is always "editor" — users can be promoted to admin later
  // via the toggle on each grant row.
  const pickedRole = "editor";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements/access");
      setGrants(data || []);
    } catch (e) { toast.error(formatError(e)); }
  }, []);
  useEffect(() => { if (open) { load(); setPickedIds([]); } }, [open, load]);

  const grantBulk = async () => {
    if (pickedIds.length === 0) { toast.error("Pick at least one user"); return; }
    setBusy(true);
    let ok = 0; let fail = 0;
    for (const uid of pickedIds) {
      try {
        const fd = new FormData();
        fd.append("user_id", uid);
        fd.append("access_role", pickedRole);
        await api.post("/announcements/access", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch { fail += 1; }
    }
    if (ok) toast.success(`Granted access to ${ok} user${ok > 1 ? "s" : ""}${fail ? ` · ${fail} failed` : ""}`);
    else toast.error("Failed to grant access");
    setPickedIds([]);
    await load();
    onSaved?.();
    setBusy(false);
  };

  const revoke = async (uid) => {
    if (!window.confirm("Revoke access?")) return;
    try {
      await api.delete(`/announcements/access/${uid}`);
      toast.success("Revoked");
      await load();
      onSaved?.();
    } catch (e) { toast.error(formatError(e)); }
  };

  // Toggle a grant's role between editor and admin — re-uses POST /access
  // (upsert behaviour on the backend).
  const changeRole = async (uid, currentRole) => {
    const next = currentRole === "admin" ? "editor" : "admin";
    try {
      const fd = new FormData();
      fd.append("user_id", uid);
      fd.append("access_role", next);
      await api.post("/announcements/access", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Set to ${next}`);
      await load();
      onSaved?.();
    } catch (e) { toast.error(formatError(e)); }
  };

  // /directory already returns ONLY active + non-temp employees. Exclude any
  // user who already has a grant so the picker never lists them twice.
  const grantedIds = useMemo(() => new Set(grants.map((g) => g.user_id)), [grants]);
  const availableUsers = useMemo(
    () => users.filter((u) => !grantedIds.has(u.id)),
    [users, grantedIds]
  );

  const pickedUsers = useMemo(
    () => pickedIds.map((id) => availableUsers.find((u) => u.id === id)).filter(Boolean),
    [pickedIds, availableUsers]
  );

  return (
    <>
      <Dialog open={open && !pickerOpen} onOpenChange={(v) => !v && onClose()}>
        <DialogContent
          className="max-w-4xl max-h-[92vh] overflow-y-auto"
          data-testid="ann-tab-access-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Editor Corner · Tab Access
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
            {/* LEFT: grant new access */}
            <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/60 space-y-3">
              <div className="text-[12px] uppercase tracking-wide text-slate-500 font-medium">
                Grant access
              </div>

              <div>
                <Label className="text-[12px]">Users (active enrolled &amp; non-temp only)</Label>
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="mt-1 w-full text-left rounded-lg border border-slate-300 bg-white px-3 py-2 hover:border-emerald-400"
                  data-testid="ann-tab-access-open-picker"
                >
                  {pickedIds.length === 0 ? (
                    <span className="text-slate-400">Click to select users…</span>
                  ) : (
                    <span className="text-slate-800">
                      <strong className="text-emerald-700">{pickedIds.length}</strong> selected
                    </span>
                  )}
                  <span className="float-right text-slate-400">▸ Pick</span>
                </button>
                {pickedUsers.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {pickedUsers.slice(0, 8).map((u) => (
                      <span key={u.id} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[11px]">
                        {u.name}
                        <button
                          type="button"
                          onClick={() => setPickedIds((p) => p.filter((x) => x !== u.id))}
                          className="hover:text-rose-600"
                          aria-label={`remove ${u.name}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {pickedUsers.length > 8 && (
                      <span className="text-[11px] text-slate-500 self-center">+{pickedUsers.length - 8} more</span>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-900/40 px-3 py-2 text-[11.5px] text-slate-600 dark:text-slate-400">
                All selected users are granted <strong className="text-emerald-700 dark:text-emerald-400">Editor</strong> access by default.
                You can promote any user to Admin later by toggling the <em>admin</em> switch on their row.
              </div>

              <Button
                onClick={grantBulk}
                disabled={busy || pickedIds.length === 0}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="ann-tab-access-grant"
              >
                <UserPlus className="w-4 h-4 mr-1.5" />
                {busy ? "Granting…" : `Grant Editor access to ${pickedIds.length || 0} user${pickedIds.length === 1 ? "" : "s"}`}
              </Button>
            </div>

            {/* RIGHT: current grants */}
            <div className="rounded-xl border border-slate-200">
              <div className="px-4 py-2 border-b border-slate-100 text-[12px] uppercase tracking-wide text-slate-500 font-medium">
                Current grants ({grants.length})
              </div>
              <div className="max-h-[440px] overflow-y-auto divide-y">
                {grants.length === 0 && (
                  <div className="p-6 text-slate-400 text-[13px] text-center">
                    No editors or admins granted yet.
                  </div>
                )}
                {grants.map((g) => {
                  const isAdmin = g.access_role === "admin";
                  return (
                    <div
                      key={g.id}
                      className="flex items-center justify-between gap-2 px-4 py-3"
                      data-testid={`ann-tab-access-row-${g.user_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-[13.5px] font-semibold text-slate-900 dark:text-slate-100 truncate">
                            {g.user_name}
                          </div>
                          <Badge
                            className={isAdmin
                              ? "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/50 dark:text-emerald-300 dark:border-emerald-700"
                              : "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:border-blue-700"}
                          >
                            {isAdmin ? "Admin access" : "Editor access"}
                          </Badge>
                        </div>
                        <div className="text-[11.5px] text-slate-500 dark:text-slate-400 truncate">
                          {g.user_email} · {roleLabel(g.user_role)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {/* admin toggle — green ON / red OFF */}
                        <button
                          type="button"
                          onClick={() => changeRole(g.user_id, g.access_role)}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
                            isAdmin
                              ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 shadow-[0_0_0_2px_rgba(16,185,129,0.25)]"
                              : "bg-rose-100 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 border-rose-400 hover:bg-rose-200"
                          }`}
                          title={isAdmin ? "Admin — click to demote to Editor" : "Editor — click to promote to Admin"}
                          data-testid={`ann-tab-access-toggle-${g.user_id}`}
                        >
                          <span
                            className={`relative inline-flex h-3.5 w-6 items-center rounded-full transition-colors ${isAdmin ? "bg-white/90" : "bg-rose-400"}`}
                          >
                            <span className={`inline-block h-2.5 w-2.5 transform rounded-full transition-transform ${isAdmin ? "translate-x-3 bg-emerald-600" : "translate-x-0.5 bg-white"}`} />
                          </span>
                          admin
                        </button>
                        <button
                          onClick={() => revoke(g.user_id)}
                          className="text-rose-500 hover:text-rose-700"
                          data-testid={`ann-tab-access-revoke-${g.user_id}`}
                          title="Revoke"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onDone={(ids) => setPickedIds(ids)}
        users={availableUsers}
        initialSelectedIds={pickedIds}
        title="Select users to grant access"
        roleOptions={ROLE_OPTIONS}
      />
    </>
  );
}

// ==================== MAIN PAGE ====================

export default function Announcements() {
  const { user } = useAuth();
  const currentUserId = user?.id;
  const isMaster = user?.role === "master_admin";

  const [accessRole, setAccessRole] = useState(null); // 'master_admin' | 'admin' | 'editor' | null
  const [topTab, setTopTab] = useState("announcement"); // 'announcement' | 'editor_corner'
  const [annSubTab, setAnnSubTab] = useState("current");
  const [ecSubTab, setEcSubTab] = useState("mine");
  const [view] = useGlobalViewMode();
  const [currentItems, setCurrentItems] = useState([]);
  const [oldItems, setOldItems] = useState([]);
  const [ecItems, setEcItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [detail, setDetail] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showAccess, setShowAccess] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);

  // Auto-tick every second — keeps the countdown pills on pending/scheduled
  // cards fresh even when the user doesn't interact.
  useTick(1000);

  const loadAccessRole = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements/access-role");
      setAccessRole(data.access_role);
    } catch { setAccessRole(null); }
  }, []);

  const loadCurrent = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements?bucket=current");
      setCurrentItems(data || []);
    } catch (e) { toast.error(formatError(e)); }
  }, []);
  const loadOld = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements?bucket=old");
      setOldItems(data || []);
    } catch (e) { toast.error(formatError(e)); }
  }, []);
  const loadEc = useCallback(async (sub) => {
    try {
      const { data } = await api.get(`/announcements/editor-corner?sub=${sub}`);
      setEcItems(data || []);
    } catch (e) { toast.error(formatError(e)); }
  }, []);
  const loadUsers = useCallback(async () => {
    try {
      // `/directory` returns ONLY active + non-temp enrolled employees, sorted by name.
      // See server.py::employee_directory — exactly the audience we need for the
      // Audience picker and Editor-Corner Tab Access dialog.
      const { data } = await api.get("/directory");
      setUsers(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadAccessRole(); loadCurrent(); loadUsers(); }, [loadAccessRole, loadCurrent, loadUsers]);
  useEffect(() => { if (topTab === "announcement" && annSubTab === "old") loadOld(); }, [topTab, annSubTab, loadOld]);
  useEffect(() => { if (topTab === "editor_corner") loadEc(ecSubTab); }, [topTab, ecSubTab, loadEc]);

  // If the user has no editor-corner access, force the Announcement tab (also
  // handles the case where a deep-link points at `?tab=editor_corner` for a
  // user without access, or an admin revokes access while they're on the tab).
  useEffect(() => {
    if (accessRole === null && topTab === "editor_corner") {
      setTopTab("announcement");
    }
  }, [accessRole, topTab]);

  // Deep-link support (?tab=editor_corner&sub=…) — only 2 sub-tabs remain.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const tab = q.get("tab"); const sub = q.get("sub");
    if (tab === "editor_corner") setTopTab("editor_corner");
    if (sub && ["approval", "mine"].includes(sub)) setEcSubTab(sub);
  }, []);

  // MA/admin default to Approval tab on first render; editors stay on My Posts.
  useEffect(() => {
    if ((accessRole === "master_admin" || accessRole === "admin") && ecSubTab === "mine") {
      setEcSubTab("approval");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessRole]);

  const canPost = accessRole === "master_admin" || accessRole === "admin" || accessRole === "editor";
  const showEditorCorner = canPost; // only visible to those with access

  const [pendingDeleteAnn, setPendingDeleteAnn] = useState(null);
  const onOpen = (a) => setDetail(a);
  const onDelete = (a) => setPendingDeleteAnn(a);
  const confirmDeleteAnn = async (reason) => {
    try {
      await api.delete(`/announcements/${pendingDeleteAnn.id}`, { data: { reason } });
      toast.success("Announcement moved to Recycle Bin");
      setDetail(null);
      loadCurrent(); loadOld(); loadEc(ecSubTab);
    } catch (e) { toast.error(formatError(e)); throw e; }
  };
  const onAcknowledge = async (a) => {
    try {
      await api.post(`/announcements/${a.id}/acknowledge`);
      toast.success("Acknowledged");
      setDetail(null);
      loadCurrent();
    } catch (e) { toast.error(formatError(e)); }
  };
  const onApprove = async (a) => {
    try {
      await api.post(`/announcements/${a.id}/approve`);
      toast.success("Approved");
      setDetail(null);
      loadEc(ecSubTab);
      loadCurrent();
    } catch (e) { toast.error(formatError(e)); }
  };
  const onReject = async (a, reason) => {
    try {
      const fd = new FormData(); fd.append("reason", reason);
      await api.post(`/announcements/${a.id}/reject`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Rejected");
      setDetail(null);
      loadEc(ecSubTab);
    } catch (e) { toast.error(formatError(e)); }
  };
  const onEdit = (a) => { setEditing(a); setShowNew(true); };
  const onMarkRead = async (a) => {
    if (markingRead) return;
    setMarkingRead(true);
    try {
      await api.post(`/announcements/${a.id}/mark-read`);
      toast.success("Marked as read");
      setDetail(null);
      loadCurrent();
      window.dispatchEvent(new CustomEvent("announcements:unread-refresh"));
    } catch (e) { toast.error(formatError(e)); }
    finally { setMarkingRead(false); }
  };

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <PageHeader
        moduleKey="announcements"
        title="Announcements"
        subtitle="Company-wide updates, important notices and scheduled posts."
        icon={Megaphone}
      />

      <Tabs value={topTab} onValueChange={setTopTab}>
        <TabsList className={`grid ${tabAccessibleFor(user, "announcements", "editor_corner") ? "grid-cols-2" : "grid-cols-1"} max-w-md`}>
          {tabAccessibleFor(user, "announcements", "announcement") && (
            <TabsTrigger value="announcement" data-testid="ann-tab-announcement">Announcement</TabsTrigger>
          )}
          {tabAccessibleFor(user, "announcements", "editor_corner") && (
            <TabsTrigger value="editor_corner" data-testid="ann-tab-editor-corner">
              Editor Corner
            </TabsTrigger>
          )}
        </TabsList>

        {/* ================ ANNOUNCEMENT TAB ================ */}
        {tabAccessibleFor(user, "announcements", "announcement") && (
        <TabsContent value="announcement" className="space-y-4">
          <Tabs value={annSubTab} onValueChange={setAnnSubTab}>
            <TabsList>
              {subtabAccessibleFor(user, "announcements", "announcement", "current") && (
                <TabsTrigger value="current" data-testid="ann-sub-current">Current</TabsTrigger>
              )}
              {subtabAccessibleFor(user, "announcements", "announcement", "old") && (
                <TabsTrigger value="old" data-testid="ann-sub-old">Archived</TabsTrigger>
              )}
            </TabsList>
          </Tabs>
          <GradientCard className="p-4">
            <AnnouncementList
              items={annSubTab === "current" ? currentItems : oldItems}
              view={view}
              onOpen={onOpen}
              onDelete={onDelete}
              onEdit={onEdit}
              currentUserId={currentUserId}
              isMaster={isMaster}
            />
          </GradientCard>
        </TabsContent>
        )}

        {/* ================ EDITOR CORNER TAB ================ */}
        {tabAccessibleFor(user, "announcements", "editor_corner") && (
        <TabsContent value="editor_corner" className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Tabs value={ecSubTab} onValueChange={setEcSubTab}>
              <TabsList>
                {subtabAccessibleFor(user, "announcements", "editor_corner", "mine") && (
                  <TabsTrigger value="mine" data-testid="ec-sub-mine"><FileText className="w-3.5 h-3.5 mr-1" /> My Posts</TabsTrigger>
                )}
                {subtabAccessibleFor(user, "announcements", "editor_corner", "approval") && (
                  <TabsTrigger value="approval" data-testid="ec-sub-approval"><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Approval</TabsTrigger>
                )}
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => { setEditing(null); setShowNew(true); }}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="ann-new-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" /> New Post
              </Button>
            </div>
          </div>
          <GradientCard className="p-4">
            <AnnouncementList
              items={ecItems}
              view={view}
              onOpen={onOpen}
              onDelete={onDelete}
              onEdit={onEdit}
              currentUserId={currentUserId}
              isMaster={isMaster}
            />
          </GradientCard>
        </TabsContent>
        )}
      </Tabs>

      <DetailDialog
        open={!!detail}
        onClose={() => setDetail(null)}
        ann={detail}
        isMaster={isMaster}
        onAcknowledge={onAcknowledge}
        onApprove={onApprove}
        onReject={onReject}
        onMarkRead={onMarkRead}
        accessRole={accessRole}
      />
      <NewPostDialog
        open={showNew}
        onClose={() => { setShowNew(false); setEditing(null); }}
        onCreated={() => { loadCurrent(); loadEc(ecSubTab); }}
        users={users}
        isMaster={isMaster}
        editing={editing}
      />
      {isMaster && (
        <TabAccessDialog open={showAccess} onClose={() => setShowAccess(false)} users={users} onSaved={loadAccessRole} />
      )}
      <DeleteWithReasonDialog
        open={!!pendingDeleteAnn}
        onClose={() => setPendingDeleteAnn(null)}
        title={pendingDeleteAnn ? `Delete "${pendingDeleteAnn.title}"?` : "Delete announcement?"}
        description="It will move to Admin Hub → Recycle Bin. Master Admin can restore it later."
        onConfirm={confirmDeleteAnn}
        testid="announcement-delete"
      />
    </div>
  );
}
